import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps, NextPage } from 'next';
import React, { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import classNames from 'classnames/bind';
import PostInfo from '../../../../components/post/PostInfo';
import Tags from '../../../../components/common/Tags';
import PostThumbnail from '../../../../components/post/PostThumbnail';
import PostContent from '../../../../components/post/PostContent';
import RelatedPosts from '../../../../components/post/RelatedPosts';
import { usePCWideMediaQuery } from '../../../../hooks/useResponsiveMediaQuery';
import { incrementPostViewsApi } from '../../../../apis/postsApi';
import Post, { StoredPost } from '../../../../types/post';
import { LocaleCode } from '../../../../types/locale';
import { getPostMaps, getSortedPosts } from '../../../../singletons/posts';
import { getTopTags } from '../../../../singletons/tags';
import { createPostId, getPostContentSrc } from '../../../../utils/postInfo';
import { getIndex, getRelatedPosts } from '../../../../utils/indexing';
import Log from '../../../../utils/log';
import { getOpenGraphUrl } from '../../../../utils/url';
import styles from './[slug].module.scss';

const cx = classNames.bind(styles);

export type PostPageProps = {
  post: Post;
  relatedPosts: StoredPost[];
  tags: string[];
  topTags: string[];
};

const PostPage: NextPage<PostPageProps> = ({ post, relatedPosts, tags, topTags }) => {
  const heroImageSrc = post.heroImage ? getPostContentSrc(post.heroImage) : undefined;
  const heroVideoSrc = post.heroVideo ? getPostContentSrc(post.heroVideo) : undefined;
  const isPCWide = usePCWideMediaQuery();
  const { asPath, locale, defaultLocale } = useRouter();

  // meta data
  const pageTitle = `${post.title} | CLOVA Engineering Blog`;

  // related posts나 top tags 중 아무것도 표시 안할 때는 section 자체를 숨기기
  const isBottomSectionVisible = relatedPosts?.length > 0 || !isPCWide; // TODO: tags에 대한 조건문 추가

  // increment page view
  useEffect(() => {
    (async () => {
      try {
        await incrementPostViewsApi(locale as LocaleCode, post.id);
      } catch (e) {
        Log.print({ level: 'warn', message: 'failed to increment post views' }, { exception: e });
      }
    })();
  }, [locale, post]);

  return (
    <>
      {/* meta data (_app에 등록된 것과 다르게 처리할 것만 이곳에 추가) */}
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:url" key="og:url" content={getOpenGraphUrl(asPath, locale, defaultLocale)} />
        <meta property="og:title" key="og:title" content={pageTitle} />
        <meta property="og:description" key="og:description" content={post.excerpt} />
        {heroImageSrc && <meta property="og:image" key="og:image" content={getOpenGraphUrl(heroImageSrc)} />}
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={post.excerpt} />
        {heroImageSrc && <meta name="twitter:image" content={getOpenGraphUrl(heroImageSrc)} />}
      </Head>

      {/* post 영역 */}
      <div className={cx('post')}>
        <div className={'content_wrap'}>
          <strong className={'main_category'}>{post.category}</strong>
          <h2 className={cx('post_title')}>{post.title}</h2>
          <PostInfo
            createdAt={post.createdAt}
            authors={post.authors}
            isScheduled={post.isScheduled}
            isPostPage
            isSharePostEnabled
          />
        </div>
        {post.type !== 'interview' && (heroImageSrc || heroVideoSrc) && (
          <PostThumbnail
            className={cx('post_top_image')}
            thumbUrl={heroImageSrc}
            videoUrl={heroVideoSrc}
            isScheduled={post.isScheduled}
          />
        )}
        <div className={cx('content_wrap')}>
          <div className={cx('post_inner')}>
            <div className={cx('post_content')}>
              <PostContent post={post} />
              <div className={cx('post_content_tag')}>
                <strong className={'main_category'}>Tags</strong>
                <Tags tags={tags} nClicksCode="post.tags" />
              </div>
            </div>
            {isPCWide && (
              <div className={cx('post_top_tags')}>
                <h3 className={'main_category'}>Top Tags</h3>
                <Tags tags={topTags} nClicksCode="post.toptags" />
              </div>
            )}
          </div>
        </div>
        {isBottomSectionVisible && (
          <div className={cx('related_posts')}>
            <div className={'content_wrap'}>
              {relatedPosts?.length > 0 && (
                <>
                  <h3 className="main_category">Related Posts</h3>
                  <RelatedPosts key={post.id} relatedPosts={relatedPosts} />
                </>
              )}
              {!isPCWide && (
                <div className={cx('post_top_tags')}>
                  <h3 className={'main_category'}>Top Tags</h3>
                  <Tags tags={topTags} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  // 실재하는 post로 향하는 url만 유효하다고 판단 (나머지는 404)
  // URL 구조: /{locale}/posts/{year}/{month}/{slug}
  const paths: GetStaticPathsResult['paths'] = [];
  const promises = locales?.map((locale) => getSortedPosts(locale as LocaleCode)) ?? [];

  try {
    const responses = await Promise.all(promises);
    responses.forEach(({ public: publicPosts }, index) => {
      const locale = locales?.[index] as LocaleCode;
      publicPosts.forEach(({ year, month, slug }) => {
        paths.push({ params: { year, month, slug }, locale });
      });
    });
  } catch (e) {
    Log.print({ level: 'error', message: 'an error occurred while fetching sorted posts' }, { exception: e });
  }

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  // url로부터 post의 id를 유추
  const localeCode = (locale as LocaleCode) ?? 'ko';
  const year = (params?.year as string) ?? '';
  const month = (params?.month as string) ?? '';
  const slug = (params?.slug as string) ?? '';
  const id: Post['id'] = createPostId(localeCode, year, month, slug);

  // id를 이용해 public Post object 찾기
  const { public: publicPostMap } = await getPostMaps(localeCode);
  const post = publicPostMap[id];

  // post를 찾을 수 없는 경우 404 페이지 표시
  if (!post) {
    return {
      notFound: true,
    };
  }

  // related posts 구하기 (title, plainContent를 기준으로 유사한 문서 검색)
  const index = await getIndex(localeCode);
  const relatedPosts = await getRelatedPosts(index, post, 4); // 최대 4개까지 표시

  // top tags 구하기
  const topTags = await getTopTags(localeCode);

  return {
    props: {
      post,
      relatedPosts,
      tags: post.tags,
      topTags,
      ...(await serverSideTranslations(localeCode, ['common'])),
    },
  };
};

/**
 * NOTE: 링크를 클릭하여 페이지에 진입할 경우 무조건 두 번 render가 되는 이슈가 있었습니다. (refresh 때는 한번만 render됨)
 *       단순히 render만 두 번 되는게 아니라, 'post' prop이 다르다고 인식이 되어 useEffect(..., [post])도 두 번 호출 되었습니다.
 *       문제는 그 두 번의 render 동안 post object의 내부 값이 동일했음에도 다른 object로 인식했다는 것입니다.
 *       ('post' prop은 getStaticProps를 통해 build time에 미리 정의되며 수정되지 않습니다.)
 *       첫번째 받은 post와 두번째 post가 다르게 인식되는 이유를 알 수가 없어 우선 post.id를 비교하도록 강제했습니다.
 */
export default React.memo(PostPage, (prev, next) => prev.post.id === next.post.id);
